import { useToast } from '@airship-platform/airship-patterns';
import {
  DescriptionEditable,
  ManageModifierContent,
  ManageProductSection,
} from '@airship-platform/airship-proposal';
import { EmptyState, FlexBox } from '@airship-platform/jam-web/components';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  AppointmentProposalOptionModifier,
  useAppointmentProposalOptionModifiers,
} from '../../data-access/appointmentProposalOptionModifiers';
import { useAppointmentProposalOptions } from '../../data-access/appointmentProposalOptions';
import { describeOfflineSyncError } from '../../patterns';

const EditModifierPage: React.FC = () => {
  const { appointmentProposalOptionModifierId } = useParams<{
    appointmentProposalOptionModifierId: string;
  }>();

  const { currentOption } = useAppointmentProposalOptions();
  const {
    proposalModifiers,
    isLoading,
    updateAppointmentProposalOptionModifier,
  } = useAppointmentProposalOptionModifiers();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [modifiers, setModifiers] = useState<
    AppointmentProposalOptionModifier[]
  >(
    proposalModifiers.filter(
      (r) =>
        r.appointmentProposalOptionModifierId ===
        appointmentProposalOptionModifierId
    )
  );

  if (
    !modifiers.length ||
    !currentOption ||
    !appointmentProposalOptionModifierId
  ) {
    return (
      <EmptyState
        title="Modifier not found"
        subtitle="The requested modifier could not be located or is unavailable for this appointment."
      />
    );
  }

  const isReadOnly = currentOption.isReadOnly;

  const handleUpdate =
    (modifierId: string) =>
    (updates: Partial<AppointmentProposalOptionModifier>) => {
      setModifiers((prev) =>
        prev.map((m) =>
          m.appointmentProposalOptionModifierId === modifierId
            ? { ...m, ...updates }
            : m
        )
      );
    };

  const handleSubmit = () => {
    const modifiersNames = modifiers.map((m) => m.displayName).join(', ');

    return Promise.all(
      modifiers.map((modifier) =>
        updateAppointmentProposalOptionModifier(
          modifier.appointmentId,
          modifier.appointmentProposalId,
          modifier.appointmentProposalOptionId,
          modifier.appointmentProposalOptionModifierId,
          {
            description: modifier.description,
            amount: modifier.amount,
          }
        )
      )
    ).then(() => {
      showToast(`${modifiersNames} updated`, 'success');
      navigate(-1);
    });
  };

  return (
    <FlexBox flexDirection="column" gap={2} p={2}>
      <Typography variant="h4">Update Rebate</Typography>
      {modifiers.map((modifier) => (
        <ManageProductSection
          key={modifier.appointmentProposalOptionModifierId}
          product={{
            name: modifier.displayName || 'Modifier',
            images: modifier.images,
            error: describeOfflineSyncError(modifier.offlineSyncErrors),
          }}
        >
          <ManageModifierContent
            modifier={modifier}
            isLoading={isLoading}
            isDisabled={isReadOnly}
            priceIsEditable={!isReadOnly}
            submitButtonText="Update"
            onSubmit={handleSubmit}
            onUpdateModifier={handleUpdate(
              modifier.appointmentProposalOptionModifierId
            )}
          />

          <Divider />

          <DescriptionEditable
            description={modifier.description}
            onUpdateDescription={(newDescription) =>
              handleUpdate(modifier.appointmentProposalOptionModifierId)({
                description: newDescription,
              })
            }
            isEditable={!isReadOnly}
          />
        </ManageProductSection>
      ))}
    </FlexBox>
  );
};

export default EditModifierPage;
